/**
 * Pure helpers for the League Detail Fixtures tab.
 *
 * Fixtures stay sorted by time; we segment the time-ordered list with a banner
 * whenever the round/group changes so a user can tell which group (World Cup) or
 * round a contiguous run of fixtures belongs to — without re-sorting away from time.
 */

import type { GameResult } from '../../gameResults/types/gameResults.types';

/** A mapped fixture row plus its raw API-Football `round` (e.g. "Group A - 1", "Round of 16"). */
export type LeagueFixture = GameResult & { round: string | null };

export type LeagueFixtureListItem =
  | { type: 'banner'; key: string; label: string }
  | { type: 'fixture'; key: string; fixture: LeagueFixture; isLastInSection: boolean };

/**
 * Derive the banner label from a raw `round` string.
 * - "Group A - 1" / "Group Stage - Group A" → "Group A"
 * - "Round of 16" / "Quarter-finals" / "Final" → unchanged
 * - "Regular Season - 12" → "Regular Season" (matchday suffix stripped; low-noise single banner)
 * - empty / non-string → null (no banner)
 */
export function parseFixtureBannerLabel(round: string | null | undefined): string | null {
  if (typeof round !== 'string') return null;
  const trimmed = round.trim();
  if (!trimmed) return null;

  const groupMatch = trimmed.match(/group\s+([a-z])\b/i);
  if (groupMatch) return `Group ${groupMatch[1].toUpperCase()}`;

  const withoutMatchday = trimmed.replace(/\s*-\s*\d+\s*$/, '').trim();
  return withoutMatchday.length > 0 ? withoutMatchday : trimmed;
}

/**
 * Flatten a time-ordered fixture list into banner + fixture render items.
 * A banner is emitted only when the label changes from the previous run, so
 * repeated/contiguous groups collapse under a single banner.
 */
export function buildLeagueFixtureSections(
  fixtures: readonly LeagueFixture[],
): LeagueFixtureListItem[] {
  const items: LeagueFixtureListItem[] = [];
  let currentLabel: string | null = null;

  fixtures.forEach((fixture, index) => {
    const label = parseFixtureBannerLabel(fixture.round);
    if (label !== null && label !== currentLabel) {
      items.push({ type: 'banner', key: `banner-${label}-${index}`, label });
      currentLabel = label;
    }

    const nextLabel =
      index + 1 < fixtures.length ? parseFixtureBannerLabel(fixtures[index + 1].round) : null;
    const nextStartsNewSection = nextLabel !== null && nextLabel !== currentLabel;
    const isLastInSection = index === fixtures.length - 1 || nextStartsNewSection;

    items.push({ type: 'fixture', key: `fx-${fixture.id}`, fixture, isLastInSection });
  });

  return items;
}
